import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { EmptyState } from "@/shared/components";
import { Button, Input, Select, PhoneInput } from "@/shared/ui";
import type { EmergencyContact } from "../../../types/personalSheet";
import { editEmergencyContactSchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  contacts: EmergencyContact[];
  recordsEditable?: boolean;
  draftAddedRecords?: EmergencyContact[];
}

interface EmergencyEntry {
  id: number | null;
  contact_name: string;
  contact_phone: string;
  contact_relation: string;
  birth_year: string;
}

function makeEmptyEntry(): EmergencyEntry {
  return { id: null, contact_name: "", contact_phone: "", contact_relation: "", birth_year: "" };
}

function fromRecord(contact: EmergencyContact): EmergencyEntry {
  return {
    id: contact.id,
    contact_name: contact.contact_name ?? "",
    contact_phone: contact.contact_phone ?? "",
    contact_relation: contact.contact_relation ?? "",
    birth_year: contact.birth_year != null ? String(contact.birth_year) : "",
  };
}

function isFilledEntry(entry: EmergencyEntry): boolean {
  return entry.contact_name.trim() !== "" || entry.contact_phone.trim() !== "" || entry.contact_relation.trim() !== "";
}

function parseBirthYear(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

function toApiEntry(entry: EmergencyEntry): Record<string, unknown> {
  const api: Record<string, unknown> = {
    contact_name: entry.contact_name,
    contact_phone: entry.contact_phone,
    contact_relation: entry.contact_relation,
    birth_year: parseBirthYear(entry.birth_year),
  };
  if (entry.id != null) api.id = entry.id;
  return api;
}

function entriesSignature(entries: EmergencyEntry[]): string {
  return JSON.stringify(entries.filter(isFilledEntry).map(toApiEntry));
}

const EditEmergencyContactsSection = forwardRef<SectionFormHandle, Props>(function EditEmergencyContactsSection({ contacts, recordsEditable = false, draftAddedRecords }, ref) {
  const { t } = useTranslation("EmployeePortal");

  const [newEntries, setNewEntries] = useState<EmergencyEntry[]>(() =>
    recordsEditable ? contacts.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? contacts.map(fromRecord) : []));
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});

  const relationOptions = [
    { label: t("personalSheet.relationValues.mother"), value: "mother" },
    { label: t("personalSheet.relationValues.father"), value: "father" },
    { label: t("personalSheet.relationValues.spouse"), value: "spouse" },
    { label: t("personalSheet.relationValues.son"), value: "son" },
    { label: t("personalSheet.relationValues.daughter"), value: "daughter" },
    { label: t("personalSheet.relationValues.sibling"), value: "sibling" },
    { label: t("personalSheet.relationValues.other"), value: "other" },
  ];

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: keyof EmergencyEntry, value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        if (!newEntries.some(isFilledEntry)) return null;
        const allRecords = [...contacts.map(fromRecord), ...newEntries.filter(isFilledEntry)].map(toApiEntry);
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        return null;
      },
      async validate() {
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const result = editEmergencyContactSchema.safeParse({
            contact_name: entry.contact_name,
            contact_phone: entry.contact_phone,
            contact_relation: entry.contact_relation,
          });
          if (!result.success) {
            allValid = false;
            const entryErrors: Record<string, string> = {};
            for (const issue of result.error.issues) {
              entryErrors[issue.path[0] as string] = issue.message;
            }
            errors[i] = entryErrors;
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        if (recordsEditable) return entriesSignature(newEntries) !== initialSignatureRef.current;
        return newEntries.some(isFilledEntry);
      },
    };
  });

  const showReadOnly = !recordsEditable;
  const visibleCount = (showReadOnly ? contacts.length : 0) + newEntries.length;

  return (
    <div className="flex flex-col gap-4">
      {visibleCount === 0 && (
        <EmptyState noDataText={t("personalSheet.empty.emergency")} noDataHint={t("personalSheet.empty.emergencyHint")} compact />
      )}
      {showReadOnly &&
        contacts.map((contact, index) => (
          <div key={contact.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
            <p className="text-body-bold-md content-base-primary">
              {t("personalSheet.contactNumber", { number: index + 1 })}
            </p>
            <Input label={t("personalSheet.fields.contactName")} placeholder="" value={contact.contact_name} disabled />
            <Select
              label={t("personalSheet.fields.contactRelation")}
              options={relationOptions}
              value={contact.contact_relation}
              onChange={() => {}}
              placeholder=""
              disabled
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label={t("personalSheet.fields.contactPhone")} placeholder="" value={contact.contact_phone} disabled />
              <Input
                label={t("personalSheet.fields.birthYear")}
                placeholder=""
                value={contact.birth_year != null ? String(contact.birth_year) : ""}
                disabled
              />
            </div>
          </div>
        ))}

      {newEntries.map((entry, index) => (
        <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-body-bold-md content-base-primary">
              {t("personalSheet.contactNumber", { number: (showReadOnly ? contacts.length : 0) + index + 1 })}
            </p>
            <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
              <Trash size={16} color="currentColor" />
              {t("personalSheet.removeRecord")}
            </Button>
          </div>
          <Input
            label={t("personalSheet.fields.contactName")}
            hasRequiredLabel
            placeholder={t("personalSheet.fields.contactName")}
            value={entry.contact_name}
            onChange={(e) => updateEntry(index, "contact_name", e.target.value)}
            error={validationErrors[index]?.contact_name ? t(validationErrors[index].contact_name) : undefined}
          />
          <Select
            label={t("personalSheet.fields.contactRelation")}
            hasRequiredLabel
            options={relationOptions}
            value={entry.contact_relation}
            onChange={(v) => updateEntry(index, "contact_relation", v ?? "")}
            placeholder={t("personalSheet.fields.contactRelation")}
            error={validationErrors[index]?.contact_relation ? t(validationErrors[index].contact_relation) : undefined}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhoneInput
              label={t("personalSheet.fields.contactPhone")}
              hasRequiredLabel
              value={entry.contact_phone}
              onChange={(value) => updateEntry(index, "contact_phone", value)}
              error={validationErrors[index]?.contact_phone ? t(validationErrors[index].contact_phone) : undefined}
            />
            <Input
              label={t("personalSheet.fields.birthYear")}
              placeholder={t("personalSheet.fields.birthYear")}
              onlyNumber
              value={entry.birth_year}
              onChange={(e) => updateEntry(index, "birth_year", e.target.value)}
            />
          </div>
        </div>
      ))}

      <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
        <Add size={16} color="currentColor" />
        {t("personalSheet.addEmergencyContact")}
      </Button>
    </div>
  );
});

export default EditEmergencyContactsSection;
